import { ChimeSDKMessaging } from '@aws-sdk/client-chime-sdk-messaging';
import { ChimeSDKIdentity } from '@aws-sdk/client-chime-sdk-identity';
import backendFetch from '../utils/backendFetch';
/* eslint-disable no-plusplus */
/* eslint-disable no-console */
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

// eslint-disable-next-line no-unused-vars
export const Persistence = {
  PERSISTENT: 'PERSISTENT',
  NON_PERSISTENT: 'NON_PERSISTENT'
};

export const MessageType = {
  STANDARD: 'STANDARD',
  CONTROL: 'CONTROL'
};

export class ChimeAPI {
  constructor(profile, credentialProvider, identityService) {
    this._profile = profile;
    this._credentialProvider = credentialProvider;
    this._identityService = identityService;
    this._idToken = credentialProvider.getToken();
  }
  createMemberArn = (userId = this._profile) =>
    `${process.env.NEXT_PUBLIC_ARN_INSTANCE}/user/${userId}`;

  Persistence = {
    PERSISTENT: 'PERSISTENT',
    NON_PERSISTENT: 'NON_PERSISTENT'
  };

  MessageType = {
    STANDARD: 'STANDARD',
    CONTROL: 'CONTROL'
  };

  appInstanceUserArnHeader = 'x-amz-chime-bearer';

  chimeMessaging = null;
  chimeIdentity = null;

  // Setup Chime Messaging Client lazily
  async chimeMessagingClient() {
    if (this.chimeMessaging == null) {
      this.chimeMessaging = new ChimeSDKMessaging({
        region: process.env.NEXT_PUBLIC_AWS_CHIME_REGION,
        credentials: await this._credentialProvider.getCredentialsProvider()
      });
    }
    return this.chimeMessaging;
  }

  resetAWSClients() {
    chimeMessaging = null;
    chimeIdentity = null;
  }
  // Setup Chime Identity Client lazily
  async chimeIdentityClient() {
    if (this.chimeIdentity == null) {
      this.chimeIdentity = new ChimeSDKIdentity({
        region: process.env.NEXT_PUBLIC_AWS_CHIME_REGION,
        credentials: await this._credentialProvider.getCredentialsProvider()
      });
    }
    return this.chimeIdentity;
  }
  async getMessagingSessionEndpoint() {
    const client = await this.chimeMessagingClient();
    return await client.getMessagingSessionEndpoint();
  }

  async sendChannelMessage(
    channelArn,
    messageContent,
    persistence,
    type,
    userName,
    subChannelId,
    options = null
  ) {
    console.log('sendChannelMessage called');

    const chimeBearerArn = this.createMemberArn();

    const params = {
      ChimeBearer: chimeBearerArn,
      ChannelArn: channelArn,
      Content: messageContent,
      Persistence: persistence, // Allowed types are PERSISTENT and NON_PERSISTENT
      Type: type, // Allowed types are STANDARD and CONTROL
      SubChannelId: subChannelId
    };
    if (options?.Metadata) {
      params.Metadata = options.Metadata;
    }

    const request = (await this.chimeMessagingClient()).sendChannelMessage(
      params
    );
    const response = await request;
    const sentMessage = {
      response: response,
      CreatedTimestamp: new Date(),
      Sender: {
        Arn: this.createMemberArn(this._profile),
        Name: userName
      }
    };
    return sentMessage;
  }
  async updateChannelMessage(
    channelArn,
    messageId,
    content,
    metadata,
    subChannelId
  ) {
    const chimeBearer = this.createMemberArn();
    const params = {
      ChannelArn: channelArn,
      MessageId: messageId,
      Content: content,
      Metadata: metadata,
      ChimeBearer: chimeBearer,
      SubChannelId: subChannelId
    };
    const request = (await this.chimeMessagingClient()).updateChannelMessage(
      params
    );
    return await request;
  }

  async getChannelMessage(
    channelArn,
    messageId,
    subChannelId,
    userId = this._profile
  ) {
    const chimeBearerArn = this.createMemberArn(userId);

    const params = {
      ChannelArn: channelArn,
      MessageId: messageId,
      ChimeBearer: chimeBearerArn,
      SubChannelId: subChannelId
    };

    return (await (await this.chimeMessagingClient()).getChannelMessage(params))
      .ChannelMessage;
  }

  async listChannelMessages(
    channelArn,
    subChannelId,
    nextToken,
    limit = 50,
    userId = this._profile
  ) {
    const chimeBearerArn = this.createMemberArn(userId);

    const params = {
      ChannelArn: channelArn,
      NextToken: nextToken || null,
      ChimeBearer: chimeBearerArn,
      SubChannelId: subChannelId,
      MaxResults: limit
    };

    const request = (await this.chimeMessagingClient()).listChannelMessages(
      params
    );
    const response = await request;
    const messageList = response.ChannelMessages;
    messageList.sort((a, b) =>
      a.CreatedTimestamp < b.CreatedTimestamp
        ? -1
        : a.CreatedTimestamp > b.CreatedTimestamp
        ? 1
        : 0
    );

    return {
      ChannelArn: response.ChannelArn,
      Messages: messageList.map((message) => ({
        ...message,
        ChannelArn: response.ChannelArn,
        Metadata: JSON.parse(message.Metadata || '{}')
      })),
      NextToken: response.NextToken
    };
  }

  async createChannelMembership(
    channelArn,
    subChannelId,
    memberArn = `${process.env.NEXT_PUBLIC_ARN_INSTANCE}/user/${this._profile}`,
    userId = this._profile
  ) {
    const chimeBearerArn = this.createMemberArn(userId);
    const params = {
      ChannelArn: channelArn,
      MemberArn: memberArn,
      Type: 'DEFAULT', // OPTIONS ARE: DEFAULT and HIDDEN
      ChimeBearer: chimeBearerArn,
      SubChannelId: subChannelId
    };

    const request = (await this.chimeMessagingClient()).createChannelMembership(
      params
    );
    const response = await request;
    return response.Member;
  }

  async deleteChannelMembership(
    channelArn,
    memberArn,
    subChannelId,
    userId = this._profile
  ) {
    const chimeBearerArn = this.createMemberArn(userId);

    const params = {
      ChannelArn: channelArn,
      MemberArn: memberArn,
      ChimeBearer: chimeBearerArn,
      SubChannelId: subChannelId
    };

    const request = (await this.chimeMessagingClient()).deleteChannelMembership(
      params
    );
    const response = await request;
    return response;
  }

  async createChannelBan(channelArn, memberArn, userId = this._profile) {
    const chimeBearerArn = this.createMemberArn(userId);

    const params = {
      ChannelArn: channelArn,
      MemberArn: memberArn,
      ChimeBearer: chimeBearerArn
    };

    const request = (await this.chimeMessagingClient()).createChannelBan(
      params
    );
    const response = await request;
    return response;
  }

  async deleteChannelBan(channelArn, memberArn, userId = this._profile) {
    const chimeBearerArn = this.createMemberArn(userId);
    const params = {
      ChannelArn: channelArn,
      MemberArn: memberArn,
      ChimeBearer: chimeBearerArn
    };

    const request = (await this.chimeMessagingClient()).deleteChannelBan(
      params
    );
    const response = await request;
    return response;
  }

  async listChannelBans(
    channelArn,
    maxResults,
    nextToken,
    userId = this._profile
  ) {
    const chimeBearerArn = this.createMemberArn(userId);
    const params = {
      ChannelArn: channelArn,
      MaxResults: maxResults,
      NextToken: nextToken,
      ChimeBearer: chimeBearerArn
    };

    const request = (await this.chimeMessagingClient()).listChannelBans(params);
    const response = await request;
    return response;
  }

  async listChannelMemberships(
    channelArn,
    subChannelId,
    userId = this._profile
  ) {
    const chimeBearerArn = this.createMemberArn(userId);

    const params = {
      ChannelArn: channelArn,
      ChimeBearer: chimeBearerArn,
      SubChannelId: subChannelId
    };

    const request = (await this.chimeMessagingClient()).listChannelMemberships(
      params
    );
    const response = await request;
    // TODO API
    const users = await Promise.all(
      response.ChannelMemberships.map((membership) =>
        this._identityService
          .getUserDetail(membership.Member.Name, ['name'])
          .then((user) => ({ user, membership }))
      )
    );
    return users.map(({ membership, user }) => ({
      ...membership,
      Member: {
        ...membership.Member,
        Name:
          user?.Attributes.find((attr) => attr.Name === 'name')?.Value ||
          membership.Member.Name,
        Username: membership.Member.Name
      }
    }));
  }

  async createChannel(
    appInstanceArn,
    metaData,
    name,
    mode,
    privacy,
    userId = this._profile
  ) {
    const chimeBearerArn = this.createMemberArn(userId);
    let metadata = metaData;
    if (!metadata && privacy === 'PUBLIC') {
      const channelType = 'PUBLIC_STANDARD';
      metadata = JSON.stringify({ ChannelType: channelType });
    }

    const params = {
      AppInstanceArn: appInstanceArn,
      Metadata: metadata,
      Name: name,
      Mode: mode,
      Privacy: privacy,
      ChimeBearer: chimeBearerArn
    };
    const request = (await this.chimeMessagingClient()).createChannel(params);
    const response = await request;
    return response.ChannelArn;
  }

  async describeChannel(channelArn, userId = this._profile) {
    const chimeBearerArn = this.createMemberArn(userId);

    const params = {
      ChannelArn: channelArn,
      ChimeBearer: chimeBearerArn
    };

    const request = (await this.chimeMessagingClient()).describeChannel(params);
    const response = await request.catch(() => undefined);
    return (
      response && {
        ...response.Channel,
        Metadata: JSON.parse(response.Channel.Metadata || '{}')
      }
    );
  }

  async updateChannel(
    channelArn,
    name,
    mode,
    metadata,
    userId = this._profile
  ) {
    const chimeBearerArn = this.createMemberArn(userId);
    const params = {
      ChannelArn: channelArn,
      Name: name,
      Mode: mode,
      Metadata: metadata,
      ChimeBearer: chimeBearerArn
    };

    const request = (await this.chimeMessagingClient()).updateChannel(params);
    const response = await request;
    return response;
  }

  async listChannelMembershipsForAppInstanceUser(userId = this._profile) {
    const chimeBearerArn = this.createMemberArn(userId);

    const params = {
      ChimeBearer: chimeBearerArn
    };

    const request = (
      await this.chimeMessagingClient()
    ).listChannelMembershipsForAppInstanceUser(params);
    const response = await request;
    const channels = response.ChannelMemberships;
    return channels
      .map((channel) => ({
        ...channel,
        ChannelSummary: {
          ...channel.ChannelSummary,
          Metadata: JSON.parse(channel.ChannelSummary.Metadata || '{}')
        }
      }))
      .filter((channel) => !channel.ChannelSummary.Metadata?.isHidden);
  }

  async updateReadMarker(channelArn, userId = this._profile) {
    // TODO 権限
    const chimeBearerArn = this.createMemberArn(userId);

    const params = {
      ChannelArn: channelArn,
      ChimeBearer: chimeBearerArn
    };

    const request = (await this.chimeMessagingClient()).updateChannelReadMarker(
      params
    );
    const response = await request;
    const channels = response.ChannelMemberships;
    return channels;
  }

  async listChannels(
    appInstanceArn = process.env.NEXT_PUBLIC_ARN_INSTANCE,
    userId = this._profile
  ) {
    const chimeBearerArn = this.createMemberArn(userId);
    const params = {
      AppInstanceArn: appInstanceArn,
      ChimeBearer: chimeBearerArn
    };

    const request = (await this.chimeMessagingClient()).listChannels(params);
    const response = await request;
    return response.Channels.map((channel) => ({
      ...channel,
      Metadata: JSON.parse(channel.Metadata || '{}')
    })).filter((channel) => !channel.Metadata?.isHidden);
  }

  async listChannelsModeratedByAppInstanceUser(userId = this._profile) {
    const chimeBearerArn = this.createMemberArn(userId);
    const params = {
      ChimeBearer: chimeBearerArn
    };
    const request = (
      await this.chimeMessagingClient()
    ).listChannelsModeratedByAppInstanceUser(params);
    const response = await request;
    return response.Channels.map((channel) => ({
      ...channel,
      Metadata: JSON.parse(channel.Metadata || '{}')
    })).filter((channel) => !channel.Metadata?.isHidden);
  }

  async listSubChannels(channelArn, userId = this._profile) {
    const chimeBearerArn = this.createMemberArn(userId);

    const params = {
      ChannelArn: channelArn,
      ChimeBearer: chimeBearerArn
    };

    const request = (await this.chimeMessagingClient()).listSubChannels(params);
    const response = await request;
    return response;
  }

  async listChannelsForAppInstanceUser(userId = this._profile) {
    const chimeBearerArn = this.createMemberArn(userId);
    const params = {
      ChimeBearer: chimeBearerArn
    };
    const request = (
      await this.chimeMessagingClient()
    ).listChannelMembershipsForAppInstanceUser(params);
    const response = await request;
    return response.ChannelMemberships.map((channel) => ({
      ...channel.ChannelSummary,
      ...channel.AppInstanceUserMembershipSummary,
      Metadata: JSON.parse(channel.ChannelSummary.Metadata || '{}')
    })).filter((channel) => !channel.Metadata?.isHidden);
  }

  async deleteChannel(channelArn, userId = this._profile) {
    const chimeBearerArn = this.createMemberArn(userId);
    const params = {
      ChannelArn: channelArn,
      ChimeBearer: chimeBearerArn
    };

    const request = (await this.chimeMessagingClient()).deleteChannel(params);
    await request;
  }
  async listChannelModerators(channelArn, userId = this._profile) {
    const chimeBearerArn = this.createMemberArn(userId);

    const params = {
      ChannelArn: channelArn,
      ChimeBearer: chimeBearerArn
    };

    const request = (await this.chimeMessagingClient()).listChannelModerators(
      params
    );
    const response = await request;
    return response ? response.ChannelModerators : null;
  }

  async registerAppInstanceUserEndpoint(
    pushId,
    name = pushId,
    userId = this.profile
  ) {
    try {
      const params = {
        AppInstanceUserArn: this.createMemberArn(userId),
        Type: 'GCM',
        ResourceArn: process.env.NEXT_PUBLIC_PINPOINT_ARN,
        AllowMessages: 'ALL',
        EndpointAttributes: { DeviceToken: pushId },
        Name: name || pushId
      };
      const request = (
        await this.chimeIdentityClient()
      )?.registerAppInstanceUserEndpoint(params);
      return await request;
    } catch (e) {
      return new Promise((resolve) => resolve(null));
    }
  }

  async deregisterAppInstanceUserEndpoint(endpointId, userId = this.profile) {
    try {
      const params = {
        AppInstanceUserArn: this.createMemberArn(userId),
        EndpointId: endpointId
      };
      const request = (
        await this.chimeIdentityClient()
      )?.deregisterAppInstanceUserEndpoint(params);
      return await request;
    } catch (e) {
      console.error(e);
      return new Promise((resolve) => resolve(null));
    }
  }

  async redactChannelMessage(channelArn, messageId, subChannelId) {
    const chimeBearerArn = this.createMemberArn();
    const params = {
      ChannelArn: channelArn,
      MessageId: messageId,
      ChimeBearer: chimeBearerArn,
      SubChannelId: subChannelId
    };

    const request = (await this.chimeMessagingClient()).redactChannelMessage(
      params
    );

    const response = await request;
    return response;
  }

  async createMeeting(name, channelArn, userId = this._profile) {
    const response = await backendFetch(
      `create?name=${encodeURIComponent(name)}&userId=${encodeURIComponent(
        userId
      )}&channel=${encodeURIComponent(channelArn)}`,
      {
        method: 'POST'
      },
      idToken
    ).then(({ result }) => result);
    const data = await response.json();

    if (data.error) {
      throw new Error(`Server error: ${data.error}`);
    }

    return data;
  }

  async createAttendee(name, channelArn, meeting, userId = this._profile) {
    const response = await backendFetch(
      `join?name=${encodeURIComponent(name)}&userId=${encodeURIComponent(
        userId
      )}&channel=${encodeURIComponent(channelArn)}&meeting=${encodeURIComponent(
        meeting
      )}`,
      {
        method: 'POST'
      },
      idToken
    ).then(({ result }) => result);
    const data = await response.json();

    if (data.error) {
      throw new Error(`Server error: ${data.error}`);
    }

    return data;
  }

  createGetAttendeeCallback() {
    return async (chimeAttendeeId, externalUserId) => {
      return {
        name: externalUserId
      };
    };
  }

  async endMeeting(meetingId) {
    const res = await backendFetch(
      `end?meetingId=${encodeURIComponent(meetingId)}`,
      {
        method: 'POST'
      },
      idToken
    ).then(({ result }) => result);

    if (!res.ok) {
      throw new Error('Server error ending meeting');
    }
  }
}
